import { useEffect, useState } from "react";
import { api, fmtMoney } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus, Trash2, Sparkles, ArrowRight, Mail, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BUYER_STATUS = [
    { v: "not_contacted", label: "Not contacted" },
    { v: "teaser_sent", label: "Teaser sent" },
    { v: "nda_signed", label: "NDA signed" },
    { v: "cim_shared", label: "CIM shared" },
    { v: "ioi_received", label: "IOI received" },
    { v: "dropped", label: "Dropped" },
];

const WARMTH_STYLE = {
    hot:     "bg-red-50 text-red-700 border-red-200",
    warm:    "bg-amber-50 text-amber-700 border-amber-200",
    warming: "bg-yellow-50 text-yellow-700 border-yellow-200",
    cold:    "bg-blue-50 text-blue-600 border-blue-200",
};

export default function BuyersTab({ dealId }) {
    const [items, setItems] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [selectedBuyer, setSelectedBuyer] = useState("");
    const [matches, setMatches] = useState([]);
    const [matchesLoaded, setMatchesLoaded] = useState(false);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [selectedMatchIds, setSelectedMatchIds] = useState(new Set());
    const [bulkLoading, setBulkLoading] = useState(false);
    const [outreachModal, setOutreachModal] = useState(null);
    const [outreachLoading, setOutreachLoading] = useState(false);
    const [outreachData, setOutreachData] = useState(null);
    const [warmthScores, setWarmthScores] = useState({});

    const refresh = async () => {
        const [b, c] = await Promise.all([api.get(`/deals/${dealId}/buyers`), api.get(`/contacts?type=buyer`)]);
        setItems(b.data);
        setContacts(c.data);
    };
    // Batch-fetch warmth scores whenever tracked buyers change
    useEffect(() => {
        if (!items.length) return;
        const ids = items.map(i => i.buyer_id).filter(Boolean);
        if (!ids.length) return;
        api.get(`/contacts/warmth-scores?ids=${ids.join(",")}`)
            .then(r => setWarmthScores(r.data || {}))
            .catch(() => {});
    }, [items]);
    const loadMatches = async () => {
        setMatchesLoading(true);
        try {
            const { data } = await api.get(`/deals/${dealId}/buyer-matches`);
            setMatches(data);
            setMatchesLoaded(true);
            setSelectedMatchIds(new Set());
            if (data.length === 0) toast.info("No uncontacted matched buyers (≥ 25)");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load matches");
        } finally {
            setMatchesLoading(false);
        }
    };
    useEffect(() => { refresh(); }, [dealId]);

    const toggleMatch = (id) => {
        setSelectedMatchIds((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleSelectAll = () => {
        if (selectedMatchIds.size === matches.length) setSelectedMatchIds(new Set());
        else setSelectedMatchIds(new Set(matches.map((m) => m.buyer_id)));
    };

    const bulkAdd = async () => {
        if (selectedMatchIds.size === 0) return;
        setBulkLoading(true);
        try {
            const { data } = await api.post(`/deals/${dealId}/buyer-matches/add-to-pipeline`, {
                buyer_ids: Array.from(selectedMatchIds),
            });
            toast.success(`Added ${data.added_count} buyer(s)${data.skipped_count ? ` · skipped ${data.skipped_count}` : ""}`);
            data.skipped.forEach((s) => toast.warning(`${s.buyer_name || s.buyer_id}: ${s.reason}`));
            setSelectedMatchIds(new Set());
            refresh();
            loadMatches();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBulkLoading(false);
        }
    };

    const addBuyer = async () => {
        if (!selectedBuyer) return;
        try {
            await api.post(`/deals/${dealId}/buyers`, { buyer_id: selectedBuyer, status: "not_contacted" });
            setSelectedBuyer("");
            refresh();
            if (matchesLoaded) loadMatches();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };
    const updateStatus = async (tid, status) => {
        await api.patch(`/deals/${dealId}/buyers/${tid}`, { status });
        refresh();
    };
    const removeBuyer = async (tid) => {
        await api.delete(`/deals/${dealId}/buyers/${tid}`);
        refresh();
        if (matchesLoaded) loadMatches();
    };

    const draftOutreach = async (trackingItem) => {
        setOutreachModal(trackingItem);
        setOutreachData(null);
        setOutreachLoading(true);
        try {
            const { data } = await api.post("/ai/buyer-fit", {
                contact_id: trackingItem.buyer_id,
                deal_id: dealId,
            });
            setOutreachData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "AI outreach generation failed");
            setOutreachModal(null);
        } finally {
            setOutreachLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
    };

    const availableBuyers = contacts.filter((c) => !items.some((i) => i.buyer_id === c.id));

    return (
        <>
        <div className="space-y-4 max-w-5xl">
            <div className="border border-border rounded-md bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold tracking-tight">Tracked buyers ({items.length})</h3>
                    <Button variant="outline" onClick={loadMatches} disabled={matchesLoading} data-testid="match-buyers-btn">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        {matchesLoading ? "Matching…" : matchesLoaded ? "Refresh matches" : "Find matched buyers"}
                    </Button>
                </div>
                <div className="flex gap-2 mb-4">
                    <Select value={selectedBuyer} onValueChange={setSelectedBuyer}>
                        <SelectTrigger><SelectValue placeholder="Select a buyer to track…" /></SelectTrigger>
                        <SelectContent>
                            {availableBuyers.length === 0 ? (
                                <SelectItem value="none" disabled>No buyers available</SelectItem>
                            ) : availableBuyers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ""}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button onClick={addBuyer} disabled={!selectedBuyer} data-testid="add-buyer-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                </div>
                {items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                        No buyers tracked yet.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left py-2">Buyer</th><th className="text-left py-2">Company</th><th className="text-left py-2">Warmth</th><th className="text-left py-2">Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => {
                                const w = warmthScores[t.buyer_id];
                                return (
                                <tr key={t.id} className="border-b border-border/60">
                                    <td className="py-3">{t.buyer?.name || "—"}</td>
                                    <td className="py-3 text-muted-foreground">{t.buyer?.company || "—"}</td>
                                    <td className="py-3">
                                        {w && !w.insufficient_data
                                            ? <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border font-medium ${WARMTH_STYLE[w.band] || ""}`} data-testid={`warmth-badge-buyer-${t.buyer_id}`}>{w.band}</span>
                                            : <span className="text-[10px] text-muted-foreground">—</span>}
                                    </td>
                                    <td className="py-3">
                                        <Select value={t.status} onValueChange={(v) => updateStatus(t.id, v)}>
                                            <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {BUYER_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button size="icon" variant="ghost" onClick={() => draftOutreach(t)} title="Draft AI outreach email" data-testid={`draft-outreach-btn-${t.id}`}>
                                                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => removeBuyer(t.id)} data-testid={`remove-buyer-btn-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {matchesLoaded && (
                <div className="border border-gold/40 rounded-md bg-white p-6" data-testid="matched-buyers-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-gold" /> Mandate-matched buyers
                            </h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Uncontacted buyers scoring ≥ 25 against this deal. {matches.length} found.
                            </p>
                        </div>
                        {matches.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={toggleSelectAll} data-testid="match-select-all-btn">
                                    {selectedMatchIds.size === matches.length ? "Clear all" : "Select all"}
                                </Button>
                                <Button
                                    onClick={bulkAdd}
                                    disabled={selectedMatchIds.size === 0 || bulkLoading}
                                    className="bg-foreground text-background hover:bg-neutral-800 hover:text-white gap-1.5"
                                    data-testid="match-bulk-add-btn"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Add to pipeline ({selectedMatchIds.size})
                                </Button>
                            </div>
                        )}
                    </div>
                    {matches.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md" data-testid="matches-empty">
                            No uncontacted matches. Either every matched buyer is already in the pipeline, or no buyer in your CRM scores ≥ 25 yet — try setting mandate sectors / geographies on your buyer contacts.
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="match-results">
                            {matches.map((m) => (
                                <li
                                    key={m.buyer_id}
                                    className={`flex items-start gap-3 p-3 border rounded-md transition-colors ${selectedMatchIds.has(m.buyer_id) ? "border-gold/60 bg-gold/5" : "border-border hover:bg-muted/30"}`}
                                    data-testid={`match-row-${m.buyer_id}`}
                                >
                                    <Checkbox
                                        checked={selectedMatchIds.has(m.buyer_id)}
                                        onCheckedChange={() => toggleMatch(m.buyer_id)}
                                        className="mt-1"
                                        data-testid={`match-checkbox-${m.buyer_id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Link to={`/contacts/${m.buyer_id}`} className="font-medium text-sm hover:text-gold transition-colors inline-flex items-center gap-1">
                                                {m.name}
                                                <ArrowRight className="w-3 h-3 opacity-60" />
                                            </Link>
                                            {m.company && <span className="text-xs text-muted-foreground">· {m.company}</span>}
                                            {(m.tags || []).slice(0, 2).map((t) => (
                                                <Badge key={t} variant="outline" className="text-[10px] uppercase">{t}</Badge>
                                            ))}
                                        </div>
                                        {m.reasons?.length > 0 && (
                                            <div className="text-[11px] text-muted-foreground mt-1">{m.reasons.join(" · ")}</div>
                                        )}
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
                                            {(m.mandate_sectors?.length > 0) && <span>SECTORS: {m.mandate_sectors.join(", ")}</span>}
                                            {(m.mandate_geographies?.length > 0) && <span>GEOS: {m.mandate_geographies.join(", ")}</span>}
                                            {(m.ticket_size_min || m.ticket_size_max) && (
                                                <span>TICKET: {m.ticket_size_min ? fmtMoney(m.ticket_size_min) : "—"}–{m.ticket_size_max ? fmtMoney(m.ticket_size_max) : "—"}</span>
                                            )}
                                            {(m.mandate_ebitda_min || m.mandate_ebitda_max) && (
                                                <span>EBITDA: {m.mandate_ebitda_min ? fmtMoney(m.mandate_ebitda_min) : "—"}–{m.mandate_ebitda_max ? fmtMoney(m.mandate_ebitda_max) : "—"}</span>
                                            )}
                                        </div>
                                    </div>
                                    <Badge
                                        className={`font-mono ml-2 ${m.score >= 80 ? "bg-emerald-100 text-emerald-700 border-emerald-300" : m.score >= 40 ? "bg-gold/20 text-gold border-gold/40" : "bg-muted text-muted-foreground"}`}
                                        data-testid={`match-score-${m.buyer_id}`}
                                    >
                                        Score {m.score}
                                    </Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>

        <Dialog open={!!outreachModal} onOpenChange={(o) => !o && setOutreachModal(null)}>
            <DialogContent className="max-w-2xl" data-testid="outreach-modal">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        AI Outreach — {outreachModal?.buyer?.name || "Buyer"}
                    </DialogTitle>
                </DialogHeader>
                {outreachLoading ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                        <Sparkles className="w-5 h-5 mx-auto mb-2 animate-pulse text-gold" />
                        Analysing buyer fit with Claude…
                    </div>
                ) : outreachData ? (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">Likelihood to bid</span>
                            <Badge className={`font-mono ${outreachData.likelihood_to_bid >= 70 ? "bg-emerald-100 text-emerald-700 border-emerald-300" : outreachData.likelihood_to_bid >= 40 ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-red-100 text-red-700 border-red-300"}`}>
                                {outreachData.likelihood_to_bid}%
                            </Badge>
                            {outreachData.likelihood_rationale && (
                                <span className="text-xs text-muted-foreground italic">{outreachData.likelihood_rationale}</span>
                            )}
                        </div>
                        {outreachData.why_match && (
                            <div className="p-3 rounded-md bg-muted/40 border border-border/60">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Why this deal fits</div>
                                <p className="text-sm leading-relaxed">{outreachData.why_match}</p>
                            </div>
                        )}
                        {outreachData.key_concerns?.length > 0 && (
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Key concerns</div>
                                <ul className="space-y-0.5">
                                    {outreachData.key_concerns.map((c, i) => (
                                        <li key={i} className="text-sm text-muted-foreground flex gap-2"><span>•</span>{c}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {outreachData.suggested_outreach && (
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Suggested outreach email</div>
                                    <Button size="sm" variant="ghost" className="h-6 text-xs gap-1.5" onClick={() => copyToClipboard(outreachData.suggested_outreach)} data-testid="copy-outreach-btn">
                                        <Copy className="w-3 h-3" /> Copy
                                    </Button>
                                </div>
                                <pre className="text-xs bg-muted/50 border border-border rounded-md p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto" data-testid="outreach-email-text">{outreachData.suggested_outreach}</pre>
                            </div>
                        )}
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
        </>
    );
}
